/**
 * ProposalDetailsModal - Modal de detalhes de uma proposta comercial
 *
 * Layout "Grid + rodapé fixo":
 *   - Cabeçalho fixo: identificação (número/versão/situação) + ações de PDF;
 *   - Barra do link público: campo somente-leitura com Copiar/Abrir;
 *   - Corpo rolável em grade: 3 colunas de conteúdo (KPIs + tabelas de itens)
 *     e 1 coluna lateral (engajamento + histórico);
 *   - Rodapé fixo: subtotal, recorrente (MRR) e total da proposta.
 *
 * Todos os dados vêm da RPC get_proposal_details (SECURITY DEFINER) — este
 * componente é 100% apresentação.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-03 v3.10.0 - Trava do processo (workflow) também nas propostas já
 *            existentes: "Marcar como enviada" e "Marcar como aceita" ficam desabilitadas
 *            com o motivo quando a política do negócio bloqueia a ação.
 *   RELEASE: 2026-08-25 v3.9.0 - Novo atalho no cabeçalho para o negócio de origem
 *            (prop opcional onOpenDeal). Só aparece quando o modal é aberto fora do
 *            modal de Negócio (Kanban/listagem de propostas). Sem alteração de backend.
 *   RELEASE: 2026-08-25 v3.8.0 - Cabeçalho exibe o tipo da proposta (chip com bolinha da cor
 *            cadastrada + nome) antes do número da proposta. Dado vem do bloco
 *            `proposal_type` da RPC get_proposal_details (v1.5.0). Sem alteração de backend.
 *   RELEASE: 2026-08-12 v3.7.0 - "Marcar como recusada" e "Cancelar proposta" passam a abrir
 *            diálogo de confirmação com motivo obrigatório (registrado na timeline).
 *   RELEASE: 2026-08-12 v3.6.0 - Bloqueio de exportação (Visualizar/Baixar PDF e Compartilhar)
 *            em situações que não podem gerar documento novo (substituída, cancelada, expirada,
 *            recusada) e nova ação "Cancelar proposta aceita", visível apenas para quem tem a
 *            permissão (matriz por fase do workspace ou chave de perfil), com motivo obrigatório.
 *   RELEASE: 2026-08-12 v3.5.0 - Situação "substituída" passa a contar como final (menu Ações
 *            bloqueado) e novo botão "Compartilhar" (WhatsApp Web / Chat interno) com mensagem
 *            personalizável, variáveis, link público, PDF e reaproveitamento de Ações Rápidas.
 *   RELEASE: 2026-08-12 v3.4.0 - Unificação das informações: remoção dos cards de KPI do corpo,
 *            validade/expiração movidas para o cabeçalho, rodapé na mesma ordem da aba Produtos
 *            (Subtotal, Descontos, Produtos, Recorrente) e input do link público não editável.
 *   RELEASE: 2026-08-12 v3.3.0 - Cabeçalho com grupo segmentado (Visualizar PDF / Baixar PDF /
 *            Ações), menu de ações com ícones e sem itens redundantes de link, rodapé com
 *            Total de produtos e tempo real em Engajamento/Histórico (useProposalRealtime).
 *   RELEASE: 2026-08-12 v3.2.0 - Menu "Ações" no cabeçalho com as mesmas opções da
 *            listagem (copiar/abrir/gerar link público e transições de situação:
 *            enviada, aceita, recusada, cancelar). Sem alteração de backend.
 *   RELEASE: 2026-08-11 v3.1.0 - Modal em 95vw x 97vh (mesmo do modal de Negócios),
 *            coluna lateral fixa em 360px e scroll independente por seção
 *            (conteúdo x timeline). Apenas layout.
 *   RELEASE: 2026-08-11 v3.0.0 - Refatoração completa do layout (modal maior em

 *            max-w-6xl, corpo em grade com coluna lateral de engajamento/histórico,
 *            tabelas próprias de Produtos e Serviços e rodapé fixo com totais).
 *            Sem alteração de backend.
 *   RELEASE: 2026-08-11 v2.3.0 - Remove totalizadores duplicados da seção de produtos
 *   RELEASE: 2026-08-11 v2.2.0 - Link público persistente (copiar/abrir)
 *   RELEASE: 2026-08-11 v2.1.0 - Geração e cópia do link público direto no modal
 *   RELEASE: 2026-08-11 v2.0.0 - Redesign (KPIs, itens e timeline unificada)
 *   RELEASE: 2026-08-11 v1.0.0 - Criação inicial (Fase 3 do módulo de Propostas)
 */

import React, { useState } from 'react';
import {
  Ban,
  Briefcase,
  Check,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  Loader2,
  MoreHorizontal,
  Send,
  Share2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';


import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useProposalRealtime } from '@/hooks/useProposalRealtime';

import {
  useDealProposals,
  useProposalDetails,
  useProposalPdf,
  useProposalShare,
  buildPublicProposalUrl,
  PROPOSAL_STATUS_COLORS,
  PROPOSAL_STATUS_LABELS,
  type ProposalStatus,
} from '@/hooks/useDealProposals';
import { ProposalPdfPreviewModal } from './ProposalPdfPreviewModal';
import { ProposalItemsTables } from './ProposalItemsTables';
import { ProposalEngagementPanel } from './ProposalEngagementPanel';
import { ProposalTimeline } from './ProposalTimeline';
import { ProposalShareDialog } from './ProposalShareDialog';
import { ProposalCancelAcceptedDialog } from './ProposalCancelAcceptedDialog';
import { ProposalStatusReasonDialog } from './ProposalStatusReasonDialog';
import { useCanCancelAcceptedProposal } from '@/hooks/useCanCancelAcceptedProposal';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
// v3.10.0: trava do processo explicada antes do clique
import { useWorkflowGuardCheck } from '@/hooks/useWorkflowGuardCheck';

interface ProposalDetailsModalProps {
  /** Proposta aberta (null fecha o modal) */
  proposalId: string | null;
  /** Handler de fechamento */
  onClose: () => void;
  /**
   * Abre o negócio de origem da proposta (opcional).
   * Quando informado, o cabeçalho exibe o atalho "Negócio: <título>".
   * Não deve ser passado quando o modal já é aberto DE DENTRO do modal de
   * Negócio (aba Propostas), para evitar modal dentro de modal.
   */
  onOpenDeal?: (dealId: string) => void;
}

/** Formata data/hora no padrão brasileiro */
function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Formata valores monetários */
function formatCurrency(value: number | null | undefined, currency = 'BRL'): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** Apenas a data (dd/mm/aaaa) */
function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
}

/**
 * Texto auxiliar da validade: quantos dias faltam ou há quanto tempo expirou.
 * Mesma regra usada no ProposalSummaryStrip (mantida local para o cabeçalho).
 */
function describeExpiration(value: string | null | undefined): string | null {
  if (!value) return null;
  const diffMs = new Date(value).getTime() - Date.now();
  const days = Math.round(diffMs / 86_400_000);
  if (days > 1) return `expira em ${days} dias`;
  if (days === 1) return 'expira amanhã';
  if (days === 0) return 'expira hoje';
  if (days === -1) return 'expirou ontem';
  return `expirou há ${Math.abs(days)} dias`;
}



export function ProposalDetailsModal({ proposalId, onClose, onOpenDeal }: ProposalDetailsModalProps) {
  // Define fullscreen vs. janela 95vw x 97vh (mesmo critério do modal de Negócios)
  const isMobile = useIsMobile();
  const { data, isLoading } = useProposalDetails(proposalId, !!proposalId);
  // Geração/abertura do PDF do snapshot (Fase 5)

  const { generatePdf, openPdf, getPdfSignedUrl } = useProposalPdf();
  // Geração do link público da proposta (Fase 6)
  const { sendProposal } = useProposalShare();
  // Somente a mutation de transição de situação (a listagem fica desabilitada:
  // dealId null + enabled false → nenhuma query extra é disparada)
  const { updateStatus } = useDealProposals(null, false);
  // Tempo real: engajamento e histórico/eventos se atualizam sozinhos
  useProposalRealtime(proposalId);

  const proposal = data?.proposal;
  // v3.8.0: tipo da proposta (bloco top-level retornado pela RPC get_proposal_details)
  const proposalType = data?.proposal_type as
    | { id?: string; name?: string | null; color?: string | null }
    | null
    | undefined;
  const items: any[] = data?.items ?? [];
  const history: any[] = data?.status_history ?? [];
  const events: any[] = data?.events ?? [];
  // Validade exibida no cabeçalho (substitui o card de KPI "Validade")
  const expirationHint = describeExpiration(proposal?.expires_at);
  const isExpired = !!proposal?.expires_at && new Date(proposal.expires_at).getTime() < Date.now();
  // Total de produtos (itens não recorrentes) somado a partir do snapshot
  const productsTotal = React.useMemo(
    () =>
      items
        .filter((item) => item.product_type !== 'service')
        .reduce((sum, item) => sum + (Number(item.total ?? 0) || 0), 0),
    [items],
  );
  const status = (proposal?.status ?? 'draft') as ProposalStatus;
  const pdfReady = proposal?.pdf_status === 'ready' && !!proposal?.pdf_storage_path;
  // Link só pode ser gerado enquanto a proposta está viva
  const canShare = ['draft', 'generated', 'sent'].includes(status);
  // Transições de situação só fazem sentido enquanto a proposta não está em estado final.
  // v3.5.0: 'superseded' (substituída) também é final — não aceita novas transições.
  const isFinalStatus = ['accepted', 'rejected', 'cancelled', 'expired', 'superseded'].includes(
    status,
  );
  // v3.6.0: situações em que a proposta NÃO pode mais ser exportada/divulgada.
  // Objetivo: evitar que um documento sem validade comercial continue circulando
  // (risco de fraude). "Aceita" continua exportável — é o comprovante do negócio.
  const isExportBlocked = ['superseded', 'cancelled', 'expired', 'rejected'].includes(status);
  // Compartilhar é ainda mais restrito: uma proposta aceita não deve ser
  // reenviada como oferta em aberto.
  const isShareBlocked = isExportBlocked || status === 'accepted';

  // v3.6.0: permissão dedicada para cancelar uma proposta JÁ ACEITA
  const { canCancelAccepted } = useCanCancelAcceptedProposal(
    proposalId,
    status === 'accepted',
  );

  /*
   * v3.10.0: trava do processo (workflow) aplicada às propostas já existentes.
   * A aba Produtos do negócio é a fonte de verdade: se o valor mudou e passou a
   * exigir aprovação, "aceitar"/"enviar" também ficam bloqueados aqui. A decisão
   * definitiva continua no banco; esta consulta apenas antecipa o motivo.
   */
  const dealIdOrigem = (data?.context?.deal_id as string | undefined) ?? null;
  const guardAceitar = useWorkflowGuardCheck({
    actionCode: 'crm.proposal.accept',
    rootEntityType: 'crm_deal',
    rootEntityId: dealIdOrigem,
    enabled: !!dealIdOrigem && !isFinalStatus,
  });
  const guardEnviar = useWorkflowGuardCheck({
    actionCode: 'crm.proposal.send',
    rootEntityType: 'crm_deal',
    rootEntityId: dealIdOrigem,
    enabled: !!dealIdOrigem && !isFinalStatus,
  });


  // Link já existente (persistido). Quando existe, não oferecemos gerar outro.
  const existingLink =
    proposal?.public_enabled && proposal?.public_token
      ? buildPublicProposalUrl(proposal.public_token)
      : null;

  // Controle do preview interno do PDF
  const [previewOpen, setPreviewOpen] = useState(false);
  // Link público exibido (persistido no banco — o mesmo link pode ser copiado sempre)
  const [publicLink, setPublicLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Compartilhamento (WhatsApp / Chat interno)
  const [shareOpen, setShareOpen] = useState(false);
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);
  // v3.6.0: diálogo de cancelamento de proposta aceita (motivo obrigatório)
  const [cancelAcceptedOpen, setCancelAcceptedOpen] = useState(false);
  // v3.7.0: transição destrutiva pendente de confirmação com motivo
  const [reasonAction, setReasonAction] = useState<'rejected' | 'cancelled' | null>(null);

  // Link exibido na barra: o recém-gerado ou o já existente
  const shownLink = publicLink ?? existingLink;

  /** Gera o link público (somente quando ainda não existe) e já tenta copiá-lo */
  const handleGenerateLink = async () => {
    if (!proposal) return;
    const result = await sendProposal.mutateAsync({ proposalId: proposal.id });
    setLinkCopied(false);
    setPublicLink(result.url);
    try {
      await navigator.clipboard.writeText(result.url);
      setLinkCopied(true);
    } catch {
      // Navegador pode bloquear a cópia automática — usuário copia manualmente
    }
  };

  /** Copia manualmente o link exibido */
  const handleCopyLink = async () => {
    const url = publicLink ?? existingLink;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success('Link copiado');
    } catch {
      toast.error('Copie o link manualmente');
    }
  };

  /**
   * Executa uma transição de situação (validações finais ficam no servidor).
   * v3.7.0: recusar e cancelar são ações destrutivas — abrem confirmação com
   * motivo obrigatório em vez de aplicar direto.
   */
  const handleStatus = (newStatus: 'sent' | 'accepted' | 'rejected' | 'cancelled') => {
    if (!proposal) return;
    if (newStatus === 'rejected' || newStatus === 'cancelled') {
      setReasonAction(newStatus);
      return;
    }
    updateStatus.mutate({ proposalId: proposal.id, status: newStatus });
  };

  /** Confirma a transição destrutiva com o motivo informado pelo usuário */
  const handleConfirmReason = (reason: string) => {
    if (!proposal || !reasonAction) return;
    updateStatus.mutate(
      { proposalId: proposal.id, status: reasonAction, reason },
      { onSuccess: () => setReasonAction(null) },
    );
  };

  /**
   * v3.6.0: cancela uma proposta JÁ ACEITA (ação de exceção).
   * O motivo é obrigatório e também é revalidado no servidor.
   */
  const handleCancelAccepted = (reason: string) => {
    if (!proposal) return;
    updateStatus.mutate(
      { proposalId: proposal.id, status: 'cancelled', reason },
      { onSuccess: () => setCancelAcceptedOpen(false) },
    );
  };

  /** Fecha o modal limpando o estado local do link */
  const handleClose = () => {
    setPublicLink(null);
    setLinkCopied(false);
    onClose();
  };

  return (
    <>
      <Dialog open={!!proposalId} onOpenChange={(open) => !open && handleClose()}>
        {/* Tela cheia no mobile; 95vw x 97vh no desktop (mesmo do modal de Negócios) */}
        <DialogContent
          className={cn(
            'p-0 gap-0 overflow-hidden flex flex-col',
            isMobile
              ? 'w-screen h-[100dvh] max-w-none rounded-none border-0'
              : 'max-w-[95vw] w-[95vw] h-[97vh]',
          )}
        >

          {/* ============================ CABEÇALHO ============================ */}
          <DialogHeader className="px-5 py-4 space-y-0 border-b shrink-0">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pr-8">
              <div className="min-w-0">
                <DialogTitle className="text-lg font-bold tracking-tight truncate flex items-center gap-2">
                  {proposal ? (
                    <>
                      {/* v3.8.0: chip com o tipo da proposta (cor + nome) antes do número */}
                      {proposalType?.name && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border border-border bg-muted/50 text-foreground shrink-0">
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{
                              backgroundColor: proposalType.color || 'currentColor',
                            }}
                          />
                          {proposalType.name}
                        </span>
                      )}
                      {proposal.proposal_number}{' '}
                      <span className="text-muted-foreground font-medium">
                        v{proposal.version_number}
                      </span>
                    </>
                  ) : (
                    'Proposta'
                  )}
                </DialogTitle>

                {proposal && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                    {/* Situação atual (bolinha colorida + texto) */}
                    <span className="flex items-center gap-1.5 text-xs font-medium">
                      <span className={cn('h-2 w-2 rounded-full', PROPOSAL_STATUS_COLORS[status])} />
                      {PROPOSAL_STATUS_LABELS[status]}
                    </span>

                    {proposal.title && (
                      <span className="text-xs text-muted-foreground truncate max-w-[280px]">
                        {proposal.title}
                      </span>
                    )}

                    {/* v3.9.0: atalho para o negócio de origem (somente quando a
                        proposta é aberta fora do modal de Negócio) */}
                    {onOpenDeal && data?.context?.deal_id && (
                      <button
                        type="button"
                        onClick={() => {
                          const dealId = data.context.deal_id as string;
                          handleClose();
                          onOpenDeal(dealId);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline max-w-[260px]"
                        title={data?.context?.deal_title || 'Abrir negócio'}
                      >
                        <Briefcase className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {data?.context?.deal_title || 'Abrir negócio'}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </button>
                    )}

                    <span className="text-xs text-muted-foreground">
                      Criada em {formatDateTime(proposal.created_at)}
                    </span>

                    {/* Validade + expiração (movidas dos cards de KPI para o cabeçalho) */}
                    {proposal.expires_at && (
                      <span
                        className={cn(
                          'text-xs',
                          isExpired ? 'text-destructive font-medium' : 'text-muted-foreground',
                        )}
                      >
                        Válida até {formatDate(proposal.expires_at)}
                        {expirationHint ? ` (${expirationHint})` : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Ações: grupo segmentado (Visualizar PDF | Baixar PDF | Ações) */}
              {proposal && (
                <div className="flex items-center shrink-0 rounded-lg border border-border overflow-hidden divide-x divide-border bg-background">

                  {/* v3.6.0: exportação bloqueada em situações sem validade comercial */}
                  {pdfReady && !isExportBlocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 rounded-none px-3 text-xs font-medium"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Visualizar PDF
                    </Button>
                  )}
                  {!isExportBlocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 rounded-none px-3 text-xs font-medium"
                      disabled={generatePdf.isPending}
                      onClick={() =>
                        pdfReady ? openPdf(proposal.pdf_storage_path) : generatePdf.mutate(proposal.id)
                      }
                    >
                      {generatePdf.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : pdfReady ? (
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                      ) : (
                        <FileText className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {pdfReady ? 'Baixar PDF' : 'Gerar PDF'}
                    </Button>
                  )}

                  {/* Compartilhar (WhatsApp Web ou Chat interno do sistema) */}
                  {!isShareBlocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 rounded-none px-3 text-xs font-medium"
                      onClick={() => setShareOpen(true)}
                    >
                      <Share2 className="h-3.5 w-3.5 mr-1.5" />
                      Compartilhar
                    </Button>
                  )}



                  {/* Menu "Ações" — link público (quando ainda não existe) + situações */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 rounded-none px-3 text-xs font-medium"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5 mr-1.5" />
                        Ações
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {/*
                        Copiar/abrir link ficam na barra do link logo abaixo do
                        cabeçalho (evita redundância). Aqui só a geração inicial.
                      */}
                      {!shownLink && canShare && (
                        <DropdownMenuItem
                          disabled={sendProposal.isPending}
                          onClick={handleGenerateLink}
                        >
                          {sendProposal.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Link2 className="h-4 w-4 mr-2 text-muted-foreground" />
                          )}
                          Gerar link público
                        </DropdownMenuItem>
                      )}

                      {/* Transições de situação (ocultas em situação final) */}
                      {!isFinalStatus && (
                        <>
                          {!shownLink && canShare && <DropdownMenuSeparator />}
                          {['draft', 'generated'].includes(status) && (
                            <DropdownMenuItem
                              className="gap-2"
                              disabled={guardEnviar.bloqueado}
                              title={guardEnviar.bloqueado ? guardEnviar.mensagem ?? undefined : undefined}
                              onClick={() => handleStatus('sent')}
                            >
                              <Send className="h-4 w-4 text-muted-foreground" />
                              Marcar como enviada
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="gap-2"
                            disabled={guardAceitar.bloqueado}
                            title={guardAceitar.bloqueado ? guardAceitar.mensagem ?? undefined : undefined}
                            onClick={() => handleStatus('accepted')}
                          >
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            Marcar como aceita
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2"
                            onClick={() => handleStatus('rejected')}
                          >
                            <XCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            Marcar como recusada
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="gap-2 text-destructive focus:text-destructive"
                            onClick={() => handleStatus('cancelled')}
                          >
                            <Ban className="h-4 w-4" />
                            Cancelar proposta
                          </DropdownMenuItem>
                        </>
                      )}

                      {/*
                        v3.6.0: exceção — cancelar uma proposta JÁ ACEITA.
                        Só aparece se o usuário tiver a permissão dedicada
                        (matriz por fase do workspace ou chave de perfil).
                      */}
                      {status === 'accepted' && canCancelAccepted && (
                        <DropdownMenuItem
                          className="gap-2 text-destructive focus:text-destructive"
                          onClick={() => setCancelAcceptedOpen(true)}
                        >
                          <Ban className="h-4 w-4" />
                          Cancelar proposta aceita
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

              )}
            </div>
          </DialogHeader>

          {/* ======================== BARRA DO LINK PÚBLICO ==================== */}
          {shownLink && (
            <div className="px-5 py-2.5 bg-muted/40 border-b shrink-0 flex flex-col lg:flex-row lg:items-center gap-2">
              {/* Somente leitura: seleção/cópia continuam válidas, mas sem cara de campo editável */}
              <Input
                readOnly
                tabIndex={-1}
                value={shownLink}
                className="h-8 text-[11px] font-mono flex-1 bg-muted/60 cursor-default select-all focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <div className="flex items-center gap-1.5 shrink-0">
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleCopyLink}>
                  {linkCopied ? (
                    <Check className="h-3.5 w-3.5 mr-1" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 mr-1" />
                  )}
                  {linkCopied ? 'Copiado' : 'Copiar'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => window.open(shownLink, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Abrir
                </Button>
              </div>
              {/* Nota curta e discreta (só em telas largas, para não roubar espaço) */}
              <p className="hidden xl:block text-[10px] text-muted-foreground leading-tight max-w-[300px]">
                Link definitivo da proposta — válido até ela ser aceita, recusada, cancelada ou
                expirar.
              </p>
            </div>
          )}

          {/* ============================ CONTEÚDO ============================ */}
          {isLoading || !proposal ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <>
              {/*
                Corpo em duas seções com scroll independente (>= xl).
                Abaixo de xl vira coluna única com scroll no wrapper externo.
              */}
              <div className="flex-1 min-h-0 flex flex-col xl:flex-row overflow-y-auto xl:overflow-hidden">
                {/* ------------------- COLUNA PRINCIPAL (DOCUMENTO) ------------------ */}
                <div className="flex-1 min-w-0 xl:min-h-0 xl:overflow-y-auto scrollbar-thin p-5 space-y-6">
                  {/* Avisos (erro de PDF / divergência com a aba Produtos) */}
                  {proposal.pdf_status === 'error' && proposal.pdf_error && (
                    <InlineNotice variant="error">
                      Falha ao gerar o PDF: {proposal.pdf_error}
                    </InlineNotice>
                  )}

                  {data?.is_out_of_sync && (
                    <InlineNotice variant="warning">
                      Os produtos do negócio foram alterados depois desta proposta. Gere uma nova
                      versão para manter os valores sincronizados.
                    </InlineNotice>
                  )}

                  {/*
                    Os KPIs financeiros saíram daqui (v3.4.0): os valores já aparecem
                    no rodapé fixo e a validade foi movida para o cabeçalho.
                  */}



                  {/* Snapshot dos itens em tabelas separadas por tipo */}
                  <ProposalItemsTables items={items} currency={proposal.currency} />
                </div>

                {/* -------------- COLUNA LATERAL (ENGAJAMENTO + HISTÓRICO) -----------
                    Largura fixa de 360px (mesma da sidebar do modal de Negócios).
                    Engajamento fica fixo no topo; somente a timeline rola. */}
                <aside className="xl:w-[360px] shrink-0 xl:min-h-0 flex flex-col xl:overflow-hidden border-t xl:border-t-0 xl:border-l border-border bg-muted/20">
                  <div className="shrink-0 px-5 pt-5 pb-4">
                    <ProposalEngagementPanel events={events} />
                  </div>

                  <div className="flex-1 xl:min-h-0 xl:overflow-y-auto scrollbar-thin px-5 pb-5">
                    <h3 className="sticky top-0 z-[1] -mx-5 px-5 py-2 bg-muted/20 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Histórico e eventos
                    </h3>
                    <div className="pt-1">
                      <ProposalTimeline history={history} events={events} variant="compact" />
                    </div>
                  </div>
                </aside>
              </div>


              {/* ============================ RODAPÉ FIXO ========================== */}
              <div className="px-5 py-3 border-t bg-background shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Mesma sequência dos totalizadores da aba Produtos do negócio */}
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Subtotal
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {formatCurrency(proposal.subtotal, proposal.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Descontos
                    </p>
                    <p
                      className={cn(
                        'text-sm font-semibold',
                        Number(proposal.discount_total || 0) > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-foreground',
                      )}
                    >
                      {formatCurrency(proposal.discount_total, proposal.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Produtos
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {formatCurrency(productsTotal, proposal.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Recorrente
                    </p>
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {formatCurrency(proposal.mrr_total, proposal.currency)} /mês
                    </p>
                  </div>
                </div>


                {/* Total destacado no padrão "documento" */}
                <div className="rounded-xl bg-foreground text-background px-5 py-2.5">
                  <p className="text-[9px] font-semibold uppercase tracking-wider opacity-70">
                    Total da proposta
                  </p>
                  <p className="text-xl font-bold tracking-tight">
                    {formatCurrency(proposal.total, proposal.currency)}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview interno do PDF (sem download) */}
      <ProposalPdfPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        proposalId={proposal?.id ?? null}
        title={proposal?.title || proposal?.proposal_number}
        storagePath={proposal?.pdf_storage_path ?? null}
      />

      {/* Compartilhamento da proposta (WhatsApp / Chat interno) */}
      {proposal && (
        <ProposalShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          getPdfSignedUrl={getPdfSignedUrl}
          onOpenConversation={(conversationId) => setChatConversationId(conversationId)}
          context={{
            proposalId: proposal.id,
            proposalNumber: proposal.proposal_number,
            total: proposal.total,
            currency: proposal.currency,
            expiresAt: proposal.expires_at,
            dealTitle: data?.context?.deal_title ?? null,
            contactId: data?.context?.contact_id ?? null,
            contactName: data?.context?.contact_name ?? null,
            contactPhone: data?.context?.contact_phone ?? null,
            publicUrl: shownLink,
            pdfStoragePath: pdfReady ? proposal.pdf_storage_path : null,
          }}
        />
      )}

      {/* v3.7.0: confirmação com motivo para recusar/cancelar */}
      <ProposalStatusReasonDialog
        open={!!reasonAction}
        onOpenChange={(open) => !open && setReasonAction(null)}
        onConfirm={handleConfirmReason}
        isPending={updateStatus.isPending}
        title={
          reasonAction === 'rejected' ? 'Marcar proposta como recusada' : 'Cancelar proposta'
        }
        description={
          reasonAction === 'rejected'
            ? 'A proposta passará para a situação Recusada e deixará de valer como oferta em aberto.'
            : 'A proposta será cancelada e deixará de valer como documento comercial ativo.'
        }
        noticeText="O link público é desativado e a proposta não poderá mais ser aceita pelo cliente."
        reasonLabel={reasonAction === 'rejected' ? 'Motivo da recusa' : 'Motivo do cancelamento'}
        confirmLabel={reasonAction === 'rejected' ? 'Confirmar recusa' : 'Confirmar cancelamento'}
      />

      {/* v3.6.0: cancelamento de proposta aceita (motivo obrigatório) */}
      <ProposalCancelAcceptedDialog
        open={cancelAcceptedOpen}
        onOpenChange={setCancelAcceptedOpen}
        onConfirm={handleCancelAccepted}
        isPending={updateStatus.isPending}
      />

      {/* Chat interno aberto a partir do compartilhamento (mensagem já em rascunho) */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={!!chatConversationId}
        onOpenChange={(open) => !open && setChatConversationId(null)}
      />
    </>
  );
}
